import React, { useState } from "react";
import PropTypes from "prop-types";
import fordStyles from "./GlobalNavBar.module.scss";
import lincolnStyles from "./GlobalNavBar-Lincoln.module.scss";
import {
  AppBar,
  Box,
  Button,
  Grid,
  Toolbar,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { invalidateSessionAndRedirect } from "../../common/InvalidateSession";
import HasPermissionTo, { hasPermissionTo } from "../../common/HasPermissionTo";
import {
  CHECK_RECALLS,
  DEALER_SETUP,
  RESOURCE_CENTER,
} from "../../common/Constants";
import {
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  IS_USA_MARKET,
} from "../../utils/EmpUtil";
import PersonIcon from "@mui/icons-material/Person";
import DashboardMessage from "../../components/dashboard/dealerDashBoard/DashboardMessage";
import EMPBranding from "../components/EMPBranding/EMPBranding";
import EMPDialog from "../components/EMPDialog/EMPDialog";
import HelpAndSupport from "../components/EMPModal/ModalFragments/HelpAndSupport";
import * as AnalyticsService from "../services/analytics/AnalyticsService";
import {
  canAccessDeliveryTools,
  isCanadianRestrictedDeliveryToolsUser,
  isLincolnDeliveryPilotUser,
  isRestrictedDeliveryToolsUser,
} from "../../utils/lincolnDeliveryPilot";
import {
  isCanadianPreorderUser,
  usesDealerExperienceRoutes,
} from "../../utils/canadianPreorderExperience";
import { getMarketBrandPath } from "../../utils/marketBrandUtils";
import { publish } from "../../common/PubSub";
import GlobalNavSearch from "./GlobalNavSearch";
import GlobalNavLanguage from "./GlobalNavLanguage";
import GlobalNavTabs from "./GlobalNavTabs";
import { hasPartsOnlyRole } from "../hooks/usePartsOnlyRole";

const lincolnSemiboldFontFamily =
  '"Lincoln Proxima Nova Semibold", Arial, sans-serif';

const fordRegularFontFamily = '"Ford F-1", Arial, sans-serif';

const ONBOARDING_USER_TYPE = "ecomm_onboarding";

const FAQS_PERMISSION = "faqsLink";
const HELP_AND_SUPPORT_PERMISSION = "helpAndSupportModal";

const hasFordAndLincolnDealers = (dealerGroup) => {
  if (!Array.isArray(dealerGroup) || dealerGroup.length === 0) {
    return false;
  }

  let fordCount = 0;
  let lincolnCount = 0;

  dealerGroup.forEach((dealer) => {
    if (dealer?.commonId) {
      dealer.commonId.endsWith("F") && fordCount++;
      dealer.commonId.endsWith("L") && lincolnCount++;
    }
  });

  return fordCount > 0 && lincolnCount > 0;
};

const brandingWrapperStyle = {
  color: "#000",
  fontFamily: 'var(--body-1-regular-font-family, "Ford F-1")',
  fontSize: "var(--body-1-regular-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-regular-font-weight, 400)",
  lineHeight: "var(--body-1-regular-line-height, 24px)",
};

const getDealerTextStyle = (brand) => ({
  color: "#000",
  textAlign: "right",
  fontFamily: IS_FORD_BRAND(brand)
    ? 'var(--body-1-regular-font-family, "Ford F-1")'
    : '"Lincoln Proxima Nova", Arial, sans-serif',
  fontSize: "var(--body-1-regular-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-regular-font-weight, 400)",
  lineHeight: "var(--body-1-regular-line-height, 24px)",
  margin: 0,
});

const getDealerFirstNameStyle = (brand) => ({
  color: "#000",
  fontFamily: IS_FORD_BRAND(brand)
    ? fordRegularFontFamily
    : lincolnSemiboldFontFamily,
  fontSize: "var(--body-1-semibold-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-semibold-font-weight, 600)",
  lineHeight: "var(--body-1-semibold-line-height, 24px)",
});

// userDetails is the login-time snapshot; the app shells keep the active
// dealer's roles on user.roles, which change on dealer or brand switches.
const getPermissionsUserDetails = (user, userDetails) => {
  let roles = userDetails?.roles || [];

  if (Array.isArray(user?.roles)) {
    roles = user.roles;
  } else if (user?.roles && typeof user.roles === "object") {
    roles = Object.values(user.roles);
  }

  return { ...userDetails, roles };
};

// Field view names arrive as "Last, First (M.)"; dealer names as "First Last".
const getFirstName = (fullName) => {
  if (typeof fullName !== "string") {
    return fullName;
  }

  const commaIndex = fullName.indexOf(",");
  const givenNames =
    commaIndex === -1 ? fullName : fullName.slice(commaIndex + 1);

  return givenNames.trim().split(/\s+/)[0] || fullName.trim();
};

const formatDealerCode = (meta) => meta[1].concat(" | ").concat(meta[2]);

const getActiveClass = (urlPath, currentUrl, styles) => {
  if (urlPath.includes(currentUrl)) {
    return styles.active;
  }

  return undefined;
};

const styleClassesShape = PropTypes.objectOf(PropTypes.string);

const dealerShape = PropTypes.shape({
  commonId: PropTypes.string,
  dealerName: PropTypes.string,
  dealerType: PropTypes.string,
  name: PropTypes.string,
});

const userShape = PropTypes.shape({
  brand: PropTypes.string,
  commonDealerId: PropTypes.string,
  dealerGroup: PropTypes.arrayOf(dealerShape),
  dealerName: PropTypes.string,
  fullName: PropTypes.string,
  market: PropTypes.string,
  name: PropTypes.string,
  pendingActions: PropTypes.arrayOf(
    PropTypes.shape({
      actionName: PropTypes.string,
      attributes: PropTypes.shape({
        dealerIds: PropTypes.arrayOf(PropTypes.string),
      }),
    })
  ),
  permissions: PropTypes.shape({
    rolePermissions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }),
  roles: PropTypes.arrayOf(PropTypes.string),
  showAll: PropTypes.bool,
  updateBusinessSettingsTab: PropTypes.func,
  updateDashboardTab: PropTypes.func,
  userType: PropTypes.string,
});

const GlobalNavBar = (props) => {
  const { user } = props;
  const styles = IS_FORD_BRAND(user.brand) ? fordStyles : lincolnStyles;

  const updateSubNavTabs = () => {
    if (!props.isNsc) {
      user.updateBusinessSettingsTab?.(0);
      user.updateDashboardTab?.(0);
    }
  };

  const permissions = user.permissions?.rolePermissions;
  const navLinks = getNavLinks(props).filter((link) =>
    hasPermissionTo(permissions, link.perform, link.condition)
  );
  const isActiveLink = (url) =>
    Boolean(getActiveClass(props.urlPath, url, styles));

  return (
    <AppBar
      position="sticky"
      className={styles.globalNavBar}
      id="appHeader"
      data-testid="appHeader"
      sx={{ color: "text.primary", boxShadow: "none" }}
    >
      <InfoBarLayout
        brand={user.brand}
        styles={styles}
        navigation={
          !props.hideToolbar && (
            <Toolbar disableGutters variant="dense" className={styles.navBar}>
              <GlobalNavTabs
                isActive={isActiveLink}
                links={navLinks}
                onNavigate={updateSubNavTabs}
                styles={styles}
                t={props.t}
              />
            </Toolbar>
          )
        }
      >
        <Box className={styles.navActions}>
          {canSearch(props) && (
            <GlobalNavSearch
              isFordBrand={IS_FORD_BRAND(user.brand)}
              styles={styles}
            />
          )}
          <UserMenu {...props} styles={styles} />
          {IS_CANADA_MARKET(user.market) && (
            <GlobalNavLanguage styles={styles} t={props.t} />
          )}
        </Box>
      </InfoBarLayout>
    </AppBar>
  );
};

GlobalNavBar.propTypes = {
  history: PropTypes.object,
  urlPath: PropTypes.string.isRequired,
  user: userShape.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object,
  roles: PropTypes.arrayOf(PropTypes.string),
  hideToolbar: PropTypes.bool,
  changeBrand: PropTypes.func,
  changeDealer: PropTypes.func,
  fromDealerSearch: PropTypes.bool,
  isNsc: PropTypes.bool,
  setPadding: PropTypes.func,
  navigateToRoute: PropTypes.func,
  linkObjects: PropTypes.array,
  updateBusinessSettingsTab: PropTypes.func,
  updateDashboardTab: PropTypes.func,
};

// Customer search lives at /search/:keyword in the NA dealer and field view
// apps, and needs a selected dealer.
const canSearch = ({ user, fromDealerSearch, hideToolbar }) =>
  !hideToolbar &&
  !fromDealerSearch &&
  !!user.commonDealerId &&
  (usesDealerExperienceRoutes(user) || user.userType === "field_view") &&
  !isCanadianRestrictedDeliveryToolsUser(user) &&
  !hasPartsOnlyRole(user);

const getNavLinks = (props) => {
  const { user, fromDealerSearch } = props;
  const hasDealerContext = !fromDealerSearch && !!user.commonDealerId;
  const isNscUser = user.userType === "nsc";
  const isUsaDealerContext = IS_USA_MARKET(user.market) && !fromDealerSearch;
  const canShowDealerAreaLinks = hasDealerContext || isNscUser;
  const isPartsOnlyUser =
    user.roles?.length === 1 &&
    user.roles[0] === "EMP_NONVEHECOMMERCE_PARTSONLY";
  const isRestrictedDeliveryTools = isRestrictedDeliveryToolsUser(user);
  const isCanadianDeliveryTools = isCanadianRestrictedDeliveryToolsUser(user);
  const isCanadianPreorder = isCanadianPreorderUser(user);
  const isLincolnPilot = isLincolnDeliveryPilotUser(user);
  const shouldIncludeMarketBrand =
    usesDealerExperienceRoutes(user) && !fromDealerSearch;
  const getDealerPath = (path) =>
    shouldIncludeMarketBrand
      ? getMarketBrandPath(user.market, user.brand, path)
      : path;

  const navLinks = [
    {
      perform: ["dealerSearch"],
      condition: fromDealerSearch || !user.commonDealerId,
      url: "/dealer-search",
      label: "GlobalNavBar.dealerSearch",
    },
    {
      perform: ["fieldReporting"],
      condition: false,
      url: "/field-reporting",
      label: "GlobalNavBar.reporting",
    },
    {
      perform: isCanadianPreorder ? [] : ["dashBoardPage"],
      condition: canShowDealerAreaLinks && !isCanadianDeliveryTools,
      url: getDealerPath("/dashboard"),
      label: "GlobalNavBar.dashboard",
    },
    {
      perform: ["pricingPage"],
      url: "/pricing",
      label: "GlobalNavBar.pricing",
    },
    {
      perform: ["accessoriesPage"],
      condition: hasDealerContext && !isCanadianDeliveryTools,
      url: "/pricing/accessories",
      label: "GlobalNavBar.accessoriesPricing",
    },
    {
      perform: isCanadianPreorder ? [] : ["customerHandlingPage"],
      condition: hasDealerContext && !isCanadianDeliveryTools,
      url: getDealerPath("/customer-handling"),
      label: "GlobalNavBar.customerHandling",
    },
    {
      perform: ["ordersPage"],
      url: "/dealer/customer-reservations",
      label: "GlobalNavBar.orders",
    },
    {
      perform: ["businessSettingsPage"],
      condition: canShowDealerAreaLinks,
      url: "/business-settings",
      label: "GlobalNavBar.settings",
    },
    {
      perform: isCanadianPreorder ? [] : ["reportingPage"],
      condition: canShowDealerAreaLinks && !isCanadianDeliveryTools,
      url: "/reporting",
      label: "GlobalNavBar.reporting",
    },
    {
      perform: ["poReportingPage"],
      url: "/",
      label: "GlobalNavBar.reporting",
    },
    {
      perform: ["crcOrdersPage"],
      url: "/",
      label: "GlobalNavBar.orders",
    },
    {
      perform: ["dealerReporting:view"],
      condition: isUsaDealerContext,
      url: getDealerPath("/dealer-reporting"),
      label: "GlobalNavBar.reporting",
    },
    {
      perform: [],
      condition:
        hasDealerContext &&
        (user.userType !== "ecomm_onboarding" || isCanadianPreorder) &&
        canAccessDeliveryTools(user) &&
        !isPartsOnlyUser,
      url: isRestrictedDeliveryTools
        ? getDealerPath("/delivery-tools/tasks")
        : getDealerPath("/delivery-tools"),
      label: "Delivery Tools",
    },
    {
      perform: ["onboarding:nonvehicle"],
      condition: !fromDealerSearch,
      url: getDealerPath("/onboarding"),
      label: "Parts Enrollment",
    },
  ];

  if (!isLincolnPilot) {
    return navLinks;
  }

  // Lincoln USA pilot dealers only get Dashboard, Customer Handling,
  // Reporting (dealer reporting) and Delivery Tools.
  const lincolnPilotUrls = [
    getDealerPath("/dashboard"),
    getDealerPath("/customer-handling"),
    getDealerPath("/dealer-reporting"),
    getDealerPath("/delivery-tools/tasks"),
  ];

  return navLinks.filter((link) => lincolnPilotUrls.includes(link.url));
};

const InfoBarLayout = ({ brand, children, navigation, styles }) => (
  <Box className={styles.infoBar}>
    <Grid container alignItems="center" wrap="nowrap" spacing={0}>
      <Grid item xs="auto">
        <Box style={brandingWrapperStyle}>
          <EMPBranding
            showLogo
            bold
            ovalFordLogo
            width={IS_FORD_BRAND(brand) ? "80px" : "153px"}
            height={IS_FORD_BRAND(brand) ? "30px" : "55px"}
            brand={brand}
          />
        </Box>
      </Grid>
      <Grid item xs zeroMinWidth className={styles.navContainer}>
        {navigation}
      </Grid>
      <Grid item xs="auto">
        {children}
      </Grid>
    </Grid>
  </Box>
);

InfoBarLayout.propTypes = {
  brand: PropTypes.string,
  children: PropTypes.node,
  navigation: PropTypes.node,
  styles: styleClassesShape.isRequired,
};

const UserMenu = (props) => {
  const [anchorElement, setAnchorElement] = useState(null);
  const { styles, t, user } = props;
  const permissions = user.permissions?.rolePermissions;
  const isOnboarding =
    user.userType === ONBOARDING_USER_TYPE && !isCanadianPreorderUser(user);
  const isFordBrand = IS_FORD_BRAND(user.brand);
  const meta = user.commonDealerId?.split("|");
  const hasDealerCode = !!meta && meta.length > 2;

  const handleClick = (event) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const trackGlobalNavContentAction = (action) => {
    AnalyticsService.trackOnClickEvent(
      "",
      `emp:global nav:content:${action}`,
      "fv:emp:global nav:content action",
      user
    );
  };

  const handlePlaceholderClick = (action) => {
    trackGlobalNavContentAction(action);
    handleClose();
  };

  const handleBrandToggleClick = () => {
    const nextBrand = isFordBrand ? "L" : "F";
    trackGlobalNavContentAction(
      isFordBrand ? "switch to lincoln" : "switch to ford"
    );

    if (props.changeBrand) {
      props.changeBrand(nextBrand);
    } else {
      publish("switchOnboardingBrand", nextBrand);
    }

    handleClose();
  };

  const handleLogout = () => {
    invalidateSessionAndRedirect();
    trackGlobalNavContentAction("logout");
  };

  const canSwitchBrand =
    hasFordAndLincolnDealers(user.dealerGroup) &&
    (isOnboarding || (!!props.changeBrand && !!permissions?.franchiseSelector));

  return (
    <>
      <Box
        onClick={handleClick}
        onKeyDown={handleClick}
        className={`${styles.dealerBox} ${
          anchorElement ? styles.dealerBoxOpen : ""
        }`}
        data-testid="dealer-box"
      >
        <Box className={styles.menuAlign}>
          <PersonIcon className={styles.userIcon} fontSize="small" />
          <DealerGreeting brand={user.brand} fullName={user.fullName} />
        </Box>
      </Box>

      <Menu
        elevation={0}
        id="user-menu"
        anchorEl={anchorElement}
        keepMounted
        open={Boolean(anchorElement)}
        onClose={handleClose}
        disableAutoFocusItem={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className: styles.userMenuPaper,
          "data-testid": "user-menu",
        }}
        MenuListProps={{
          className: styles.userMenuList,
        }}
      >
        {(user.dealerName || hasDealerCode) && (
          <Box className={styles.userMenuHeader} data-testid="user-menu-header">
            {user.dealerName && (
              <Typography
                className={styles.userMenuDealerName}
                data-testid="user-menu-dealer-name"
              >
                {user.dealerName}
              </Typography>
            )}
            {hasDealerCode && (
              <Typography
                className={styles.userMenuDealerCode}
                data-testid="user-menu-dealer-code"
              >
                {formatDealerCode(meta)}
              </Typography>
            )}
          </Box>
        )}

        {canSwitchBrand && (
          <Box
            className={styles.userMenuBrandToggleContainer}
            data-testid="user-menu-brand-toggle"
          >
            <Button
              variant="contained"
              disableElevation
              onClick={handleBrandToggleClick}
              className={styles.userMenuBrandToggleButton}
              data-testid="user-menu-brand-toggle-button"
            >
              {isFordBrand
                ? t("GlobalNavBar.userMenu.switchToLincoln")
                : t("GlobalNavBar.userMenu.switchToFord")}
            </Button>
          </Box>
        )}

        {isOnboarding ? (
          <UserMenuItem
            styles={styles}
            label={t("GlobalNavBar.userMenu.orderManagement")}
            onClick={() => handlePlaceholderClick("emp order management")}
            testId="user-menu-order-management"
          />
        ) : (
          <>
            {meta && user.userType === "field_view" && props.changeDealer && (
              <UserMenuItem
                styles={styles}
                label={t("GlobalNavBar.changeDealer")}
                component={Link}
                to="/dealer-search"
                onClick={() => {
                  handleClose();
                  props.changeDealer();
                }}
                testId="user-menu-change-dealer"
              />
            )}

            <UserMenuLink
              styles={styles}
              action="recalls"
              href={CHECK_RECALLS}
              label={t("GlobalNavBar.userMenu.recalls")}
              permissions={permissions}
              perform={["checkRecallsLink"]}
              trackGlobalNavContentAction={trackGlobalNavContentAction}
              testId="user-menu-recalls"
            />

            <HasPermissionTo
              perform={[FAQS_PERMISSION]}
              permissions={permissions}
              render={() => (
                <PermissionsDialog
                  {...props}
                  closeMenu={handleClose}
                  trackGlobalNavContentAction={trackGlobalNavContentAction}
                />
              )}
            />
          </>
        )}

        <HasPermissionTo
          perform={[FAQS_PERMISSION]}
          permissions={permissions}
          render={() => (
            <UserMenuItem
              styles={styles}
              label={t("GlobalNavBar.userMenu.faqs")}
              onClick={() => handlePlaceholderClick("faqs")}
              testId="user-menu-faqs"
            />
          )}
        />

        {!isOnboarding && (
          <UserMenuLink
            styles={styles}
            action="dealer setup"
            href={DEALER_SETUP}
            label={t("GlobalNavBar.userMenu.dealerSetup")}
            permissions={permissions}
            perform={["dealerSetupLink"]}
            trackGlobalNavContentAction={trackGlobalNavContentAction}
            testId="user-menu-dealer-setup"
          />
        )}

        {isOnboarding ? (
          <UserMenuItem
            styles={styles}
            label={t("GlobalNavBar.userMenu.resourceCenter")}
            onClick={() => handlePlaceholderClick("resource center")}
            testId="user-menu-resource-center"
          />
        ) : (
          <UserMenuLink
            styles={styles}
            action="resource center"
            href={RESOURCE_CENTER}
            label={t("GlobalNavBar.userMenu.resourceCenter")}
            permissions={permissions}
            perform={["resourceCenterLink"]}
            trackGlobalNavContentAction={trackGlobalNavContentAction}
            testId="user-menu-resource-center"
          />
        )}

        <HasPermissionTo
          perform={[HELP_AND_SUPPORT_PERMISSION]}
          permissions={permissions}
          render={() => (
            <HelpAndSupportDialog
              closeMenu={handleClose}
              styles={styles}
              t={t}
              trackGlobalNavContentAction={trackGlobalNavContentAction}
            />
          )}
        />

        <MenuItem
          disableRipple
          className={`${styles.userMenuItem} ${styles.userMenuLogoutMenuItem}`}
          onClick={handleLogout}
          data-testid="user-menu-logout"
        >
          <Typography className={styles.userMenuLogoutText}>
            {t("GlobalNavBar.userMenu.logout")}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

UserMenu.propTypes = {
  changeBrand: PropTypes.func,
  changeDealer: PropTypes.func,
  styles: styleClassesShape.isRequired,
  t: PropTypes.func.isRequired,
  user: userShape.isRequired,
};

const UserMenuItem = ({ label, styles, testId, ...menuItemProps }) => (
  <MenuItem
    disableRipple
    className={styles.userMenuItem}
    data-testid={testId}
    {...menuItemProps}
  >
    <Typography className={styles.userMenuItemText}>{label}</Typography>
  </MenuItem>
);

UserMenuItem.propTypes = {
  label: PropTypes.string,
  styles: styleClassesShape.isRequired,
  testId: PropTypes.string,
};

const UserMenuLink = ({
  action,
  href,
  label,
  permissions,
  perform,
  styles,
  testId,
  trackGlobalNavContentAction,
}) => (
  <HasPermissionTo
    perform={perform}
    permissions={permissions}
    render={() => (
      <UserMenuItem
        styles={styles}
        label={label}
        component="a"
        target="_blank"
        rel="noopener noreferrer"
        href={href}
        onClick={() => trackGlobalNavContentAction(action)}
        testId={testId}
      />
    )}
  />
);

UserMenuLink.propTypes = {
  action: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  label: PropTypes.string,
  permissions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  perform: PropTypes.arrayOf(PropTypes.string).isRequired,
  styles: styleClassesShape.isRequired,
  testId: PropTypes.string,
  trackGlobalNavContentAction: PropTypes.func.isRequired,
};

const PermissionsDialog = (props) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    props.closeMenu();
    props.trackGlobalNavContentAction("your permissions");
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <UserMenuItem
        styles={props.styles}
        label={props.t("GlobalNavBar.userMenu.yourPermissions")}
        onClick={handleOpen}
        testId="user-menu-your-permissions"
      />
      <EMPDialog
        open={open}
        dialogTitle={props.t("GlobalNavBar.userMenu.yourPermissions")}
        hasCloseIcon
        hasActionButton
        actionButtonText={props.t("GlobalNavBar.userMenu.close")}
        onConfirm={handleClose}
        handleClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DashboardMessage
          {...props}
          userDetails={getPermissionsUserDetails(props.user, props.userDetails)}
        />
      </EMPDialog>
    </>
  );
};

PermissionsDialog.propTypes = {
  closeMenu: PropTypes.func.isRequired,
  styles: styleClassesShape.isRequired,
  t: PropTypes.func.isRequired,
  trackGlobalNavContentAction: PropTypes.func.isRequired,
  user: userShape,
  userDetails: PropTypes.object,
};

const HelpAndSupportDialog = ({
  closeMenu,
  styles,
  t,
  trackGlobalNavContentAction,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    closeMenu();
    trackGlobalNavContentAction("help and support");
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <UserMenuItem
        styles={styles}
        label={t("GlobalNavBar.userMenu.helpAndSupport")}
        onClick={handleOpen}
        testId="user-menu-help-and-support"
      />
      <EMPDialog
        open={open}
        dialogTitle={t("GlobalNavBar.userMenu.helpAndSupport")}
        hasCloseIcon
        handleClose={handleClose}
        PaperProps={{ "data-testid": "help-and-support-dialog" }}
      >
        <HelpAndSupport closeAction={handleClose} />
      </EMPDialog>
    </>
  );
};

HelpAndSupportDialog.propTypes = {
  closeMenu: PropTypes.func.isRequired,
  styles: styleClassesShape.isRequired,
  t: PropTypes.func.isRequired,
  trackGlobalNavContentAction: PropTypes.func.isRequired,
};

const DealerGreeting = ({ brand, fullName }) => (
  <Typography variant="body1" component="p" style={getDealerTextStyle(brand)}>
    Hi,{" "}
    <span style={getDealerFirstNameStyle(brand)}>{getFirstName(fullName)}</span>
  </Typography>
);

DealerGreeting.propTypes = {
  brand: PropTypes.string,
  fullName: PropTypes.string,
};

export default withTranslation("emp")(GlobalNavBar);
